import { X } from 'lucide-react'
import { useId, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'

import { cn } from '../../lib/cn'
import {
  isValidEmail,
  normalizeEmail,
  splitEmailInput,
  type EmailChipsValue,
} from '../../lib/normalization'

interface EmailChipsInputProps {
  value: EmailChipsValue
  onChange: (value: { items: string[]; draft: string }) => void
  onBlur?: () => void
  disabled?: boolean
  error?: string
  id?: string
}

export function EmailChipsInput({
  value,
  onChange,
  onBlur,
  disabled,
  error,
  id,
}: EmailChipsInputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`

  const addTokens = (raw: string) => {
    const incoming = splitEmailInput(raw)
    if (incoming.length === 0) return

    const seen = new Set(value.items.map((item) => normalizeEmail(item)))
    const nextItems = [...value.items]

    for (const token of incoming) {
      const normalized = normalizeEmail(token)
      if (!seen.has(normalized)) {
        nextItems.push(isValidEmail(normalized) ? normalized : token.trim())
        seen.add(normalized)
      }
    }

    onChange({ items: nextItems, draft: '' })
  }

  const commitDraft = () => {
    if (value.draft.trim()) addTokens(value.draft)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value
    if (/[,;\s]/u.test(nextValue)) {
      addTokens(nextValue)
    } else {
      onChange({ items: [...value.items], draft: nextValue })
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ',', ';'].includes(event.key)) {
      event.preventDefault()
      commitDraft()
      return
    }

    if (event.key === 'Backspace' && value.draft === '' && value.items.length > 0) {
      onChange({ items: value.items.slice(0, -1), draft: '' })
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text')
    if (!pasted) return
    event.preventDefault()
    addTokens(pasted)
  }

  const removeItem = (index: number) => {
    onChange({
      items: value.items.filter((_item, itemIndex) => itemIndex !== index),
      draft: value.draft,
    })
  }

  return (
    <div>
      <div
        className={cn(
          'flex min-h-12 flex-wrap items-center gap-2 rounded-xl border bg-white p-2 shadow-sm transition focus-within:border-moss focus-within:ring-2 focus-within:ring-moss',
          error ? 'border-red-600' : 'border-ink/15 hover:border-ink/25',
          disabled && 'cursor-not-allowed bg-ink/5 opacity-70',
        )}
      >
        {value.items.map((item, index) => {
          const valid = isValidEmail(item)
          return (
            <span
              key={`${item}-${index}`}
              className={cn(
                'inline-flex max-w-full items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold',
                valid
                  ? 'bg-mist text-moss'
                  : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
              )}
            >
              <span className="truncate">{item}</span>
              <button
                type="button"
                className="rounded p-0.5 hover:bg-black/5"
                onClick={() => removeItem(index)}
                disabled={disabled}
                aria-label={`Quitar ${item}`}
              >
                <X aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </span>
          )
        })}
        <input
          id={inputId}
          type="text"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          className="min-w-48 flex-1 border-0 bg-transparent px-1 py-1.5 text-sm outline-none placeholder:text-ink/40 focus:ring-0"
          value={value.draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            commitDraft()
            onBlur?.()
          }}
          placeholder={value.items.length ? 'Agregar otro correo…' : 'contacto@empresa.com'}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          disabled={disabled}
        />
      </div>
      <p className="mt-1.5 text-xs text-ink/50">Enter, coma o punto y coma para agregar.</p>
      {error && (
        <p id={errorId} className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
